/** @jsx jsx */

import { useContext, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import EditMarker from 'components/UI/EditMarker';
import { colors } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { State } from 'store/store';
import { InteractionContext } from '../../Layouts/ContainerLogical';
import { getBaseButtonStyles } from './getBaseButtonStyles';
import {
  parseStylesFromPropsAndTheme,
  updateButtonHeightForIE,
} from './buttonHelpers';

const { lPink } = colors;

const GenericButton = props => {
  //
  const interactionContext = useContext(InteractionContext) as any;

  const { focusedNodeId, theme } = useSelector((state: State) => {
    const { focusedNodeId, theme } = state;
    return {
      focusedNodeId,
      theme,
    };
  });

  const { id, label, enabled = undefined, onClick } = props;

  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    color,
    background,
    fontFamily,
    buttonShape,
    borderRadius,
    buttonShadow,
    buttonOutline,
    hoverColor,
    focusColor,
    useLargeButton,
  } = parseStylesFromPropsAndTheme(props, theme);

  // console.log('generic button :', { focusColor });

  const selected = id === focusedNodeId;
  const disabled = enabled !== undefined ? !enabled : false;

  const baseButtonStyles = getBaseButtonStyles({
    color,
    buttonShape,
    borderRadius,
    background,
    fontFamily,
    hoverColor,
    buttonOutline,
    buttonShadow,
    useLargeButton,
  }) as any;

  const genericButton = (
    <button
      ref={buttonRef}
      id={`generic-button-${id}`}
      css={css({
        ...baseButtonStyles,

        transition: 'all 0.15s',

        ...(!window.dev() && disabled
          ? {
              // potentially want to be able to customize disabled color ?
              pointerEvents: 'none',
              opacity: 0.25,
            }
          : {
              ':focus': {
                boxShadow: focusColor,
              },
            }),
      })}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );

  updateButtonHeightForIE(buttonRef.current, buttonShape);

  return window.dev() ? (
    <div
      css={css({
        position: 'relative',
        display: 'inline-flex',

        ':hover': {
          button: {
            background: hoverColor,
            border: `1px solid ${hoverColor || background}`,
          },
        },
      })}
    >
      {genericButton}

      <div
        css={css({
          position: 'absolute',
          left: -15,
          top: -10,
          width: 'calc(100% + 30px)',
          height: 'calc(100% + 20px)',
          boxShadow: selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey',
          transition: 'all 0.15s',
          cursor: 'pointer',

          ':hover': {
            boxShadow: `0 0 0 1px ${lPink}`,
          },
        })}
        onClick={e => {
          if (selected) {
            return;
          }

          setCurrentBlock({ id });
        }}
      >
        {!selected && <EditMarker />}
      </div>
    </div>
  ) : (
    genericButton
  );
};

export { GenericButton };
